import { useState } from "react";
import { Menu, X, Phone, MapPin, Clock, CheckCircle, Star, Key, Lock, DoorOpen, ShieldCheck, Wrench, LockOpen } from "lucide-react";

// Importált képek
import emergencyImg from "./assets/services/emergency.jpg";
import doorOpeningImg from "./assets/services/door-opening.jpg";
import replacementImg from "./assets/services/replacement.jpg";
import repairImg from "./assets/services/repair.jpg";
import entrepreneurImg from "./assets/reviews/entrepreneur.jpg";
import motherImg from "./assets/reviews/mother.jpg";
import pensionerImg from "./assets/reviews/pensioner.jpg";
import heroBgImg from "./assets/hero-bg.jpg";

// Képek (Pixabay források - Locksmith & Portraits)
const heroImage = heroBgImg;

const serviceImages = {
  opening: emergencyImg,
  repair: repairImg,
  replacement: replacementImg,
  door: doorOpeningImg
};

const reviewImages = {
  man1: entrepreneurImg,
  woman1: motherImg,
  man2: pensionerImg
};

const phoneNumber = "+36 1 300 9484";

const services = [
  {
    title: "Sürgősségi Zárnyitás",
    description: "Beragadt a zár? Elveszett a kulcs? Roncsolásmentes nyitás percek alatt, a nap 24 órájában.",
    icon: <Key className="w-8 h-8 text-white" />,
    image: serviceImages.opening,
    highlight: true
  },
  {
    title: "Ajtónyitás",
    description: "Becsapódott az ajtó és a kulcs belül maradt? Speciális eszközeinkkel sérülésmentesen nyitjuk.",
    icon: <DoorOpen className="w-8 h-8 text-white" />,
    image: serviceImages.door,
    highlight: false
  },
  {
    title: "Zárcsere & Szerelés",
    description: "Elavult vagy meghibásodott zárak azonnali cseréje MABISZ minősített biztonsági zárakra.",
    icon: <Lock className="w-8 h-8 text-white" />,
    image: serviceImages.replacement,
    highlight: false
  },
  {
    title: "Zárjavítás & Karbantartás",
    description: "Akadozó zárbetétek, szoruló kilincsek javítása és beállítása a hosszú élettartamért.",
    icon: <Wrench className="w-8 h-8 text-white" />,
    image: serviceImages.repair,
    highlight: false
  }
];

const reviews = [
  {
    name: "Kovács Péter",
    role: "Vállalkozó, Budapest",
    text: "Vasárnap éjjel 2-kor értem haza külföldről, és beletörtem a kulcsot a zárba. Azt hittem, az utcán alszom, de a diszpécser azonnal felvette, és 25 perc múlva ott volt a szerelő. Profi munka, korrekt ár, életmentő volt!",
    rating: 5,
    date: "2 hete",
    image: reviewImages.man1
  },
  {
    name: "Szabó Gábor",
    role: "Nyugdíjas, Budapest XI. kerület",
    text: "Már többször hívtam őket zárcsere miatt. Mindig pontosan érkeznek, tiszta munkát végeznek és elmagyarázzák, hogyan kell karbantartani a zárat. Tényleg ingyenes a kiszállás, nincs rejtett költség. Csak ajánlani tudom!",
    rating: 5,
    date: "3 napja",
    image: reviewImages.man2
  },
  {
    name: "Nagy Éva",
    role: "Családanya, Érd",
    text: "A kisfiam véletlenül magára zárta a fürdőszoba ajtót és nem tudta kinyitni. Pánikban hívtam őket, nagyon megnyugtatóak voltak. Hihetetlen gyorsan kiértek és sérülés nélkül kinyitották az ajtót. Hálás köszönet a türelemért és a gyorsaságért!",
    rating: 5,
    date: "1 hónapja",
    image: reviewImages.woman1
  }
];

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gold-500 p-2.5 rounded-xl shadow-lg shadow-gold-500/20">
                <LockOpen className="w-7 h-7 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-extrabold tracking-tight text-gray-900 leading-none">
                  0-24SOS <span className="text-gold-500">Zárszerviz</span>
                </span>
                <span className="text-xs font-medium text-gray-500 tracking-wide uppercase">Budapest & Agglomeráció</span>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#szolgaltatasok" className="text-gray-700 hover:text-gold-500 font-bold uppercase tracking-wide text-sm transition-colors">Szolgáltatások</a>
              <a href="#velemenyek" className="text-gray-700 hover:text-gold-500 font-bold uppercase tracking-wide text-sm transition-colors">Vélemények</a>
              <a href="#kapcsolat" className="text-gray-700 hover:text-gold-500 font-bold uppercase tracking-wide text-sm transition-colors">Kapcsolat</a>
              <a 
                href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                className="bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-lg font-black uppercase tracking-wider transition-all transform hover:scale-105 flex items-center gap-2 shadow-xl shadow-gold-500/30 border-b-4 border-gold-700 active:border-b-0 active:translate-y-1"
              >
                <Phone className="w-5 h-5 fill-current" />
                {phoneNumber}
              </a>
            </div>

            <button 
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-xl">
            <div className="px-4 pt-4 pb-6 space-y-3">
              <a href="#szolgaltatasok" className="block px-4 py-3 text-lg font-bold uppercase text-gray-800 hover:bg-gold-50 hover:text-gold-500 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Szolgáltatások</a>
              <a href="#velemenyek" className="block px-4 py-3 text-lg font-bold uppercase text-gray-800 hover:bg-gold-50 hover:text-gold-500 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Vélemények</a>
              <a href="#kapcsolat" className="block px-4 py-3 text-lg font-bold uppercase text-gray-800 hover:bg-gold-50 hover:text-gold-500 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Kapcsolat</a>
              <a href={`tel:${phoneNumber.replace(/\s/g, '')}`} className="block px-4 py-4 text-lg font-black uppercase text-white bg-gold-500 hover:bg-gold-600 rounded-xl mt-4 flex items-center justify-center gap-2 shadow-md border-b-4 border-gold-700 active:border-b-0 active:translate-y-1">
                <Phone className="w-6 h-6 fill-current" />
                Hívás most
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 lg:pt-0 min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage} 
            alt="Zárszerelő munka közben" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white font-bold mb-8 shadow-lg animate-pulse uppercase tracking-wider text-sm">
            <Clock className="w-4 h-4" />
            <span>SOS 0-24 Kiszállás</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-8 leading-tight tracking-tight drop-shadow-2xl">
            Nem nyílik az ajtó? <br className="hidden md:block" />
            <span className="text-gold-500">Kizárta magát?</span> <br className="md:hidden" />
            Elhagyta a kulcsát? <br />
            <span className="text-gold-500">Zárat cseréltetne?</span>
          </h1>

          <div className="flex flex-col items-center gap-8 animate-fade-in-up mb-12">
            <a 
              href={`tel:${phoneNumber.replace(/\s/g, '')}`}
              className="group relative inline-flex items-center justify-center gap-3 md:gap-4 px-8 py-4 md:px-10 md:py-5 bg-gold-500 rounded-full transition-all duration-300 hover:bg-gold-600 hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] shadow-xl border-4 border-white/10"
            >
              <div className="bg-white/20 p-2.5 md:p-3 rounded-full group-hover:rotate-12 transition-transform duration-300">
                <Phone className="w-6 h-6 md:w-8 md:h-8 text-white fill-current" />
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <span className="text-sm md:text-lg font-bold text-gold-100 uppercase tracking-wider leading-none hidden sm:inline">Hívjon most:</span>
                <span className="text-sm md:text-lg font-bold text-gold-100 uppercase tracking-wider leading-none sm:hidden">Hívás:</span>
                <span className="text-xl md:text-3xl font-extrabold text-white leading-none tracking-tight whitespace-nowrap">{phoneNumber}</span>
              </div>
            </a>
          </div>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md">
            Professzionális Zárnyitás és Zárcsere Budapesten és Pest megyében.
            <span className="block mt-2 text-gold-400 font-bold">Ingyenes kiszállás a fővárosban, akár 30 percen belül!</span>
          </p>

          <div className="flex flex-col items-center gap-8 animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl">
              <div className="flex items-center justify-center gap-3 text-white bg-white/10 backdrop-blur-md px-6 py-4 rounded-xl border border-white/10 shadow-lg">
                <Clock className="w-6 h-6 text-gold-400 flex-shrink-0" />
                <span className="font-bold text-lg">30 percen belüli érkezés</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white bg-white/10 backdrop-blur-md px-6 py-4 rounded-xl border border-white/10 shadow-lg">
                <MapPin className="w-6 h-6 text-gold-400 flex-shrink-0" />
                <span className="font-bold text-lg">Ingyenes kiszállás</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white bg-white/10 backdrop-blur-md px-6 py-4 rounded-xl border border-white/10 shadow-lg">
                <ShieldCheck className="w-6 h-6 text-gold-400 flex-shrink-0" />
                <span className="font-bold text-lg text-center leading-tight">Az általunk végzett munkára<br/>garanciát vállalunk</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Redesigned */}
      <section id="szolgaltatasok" className="py-24 bg-gray-50 relative">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-200 to-transparent opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 uppercase tracking-tight">Miben segíthetünk?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Teljeskörű zárszerviz szolgáltatás a nap 24 órájában. Legyen szó sürgősségi nyitásról vagy tervezett cseréről, ránk számíthat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <div key={index} className={`group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 ${service.highlight ? 'ring-4 ring-gold-500/20' : ''}`}>
                <div className="absolute top-0 right-0 bg-gold-500 text-white px-4 py-2 rounded-bl-2xl font-bold z-20 uppercase tracking-wider text-sm">
                  0-24 SOS
                </div>
                <div className="flex flex-col md:flex-row h-full">
                  <div className="md:w-2/5 relative overflow-hidden h-64 md:h-auto">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:bg-gradient-to-r"></div>
                    <div className="absolute bottom-4 left-4 md:top-4 md:left-4 bg-gold-500 p-3 rounded-xl shadow-lg">
                      {service.icon}
                    </div>
                  </div>
                  <div className="md:w-3/5 p-8 flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-gold-500 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {service.description}
                      </p>
                    </div>
                    <a 
                      href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                      className="inline-flex items-center justify-center w-full bg-gray-900 hover:bg-gold-500 text-white font-bold py-4 px-6 rounded-lg transition-all gap-2 uppercase tracking-wide shadow-lg border-b-4 border-black hover:border-gold-700 active:border-b-0 active:translate-y-1"
                    >
                      <Phone className="w-5 h-5" />
                      Hívás most
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section - Enhanced */}
      <section id="velemenyek" className="py-24 bg-gray-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#D4AF37_1px,transparent_1px)] [background-size:16px_16px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 uppercase tracking-tight">Ügyfélvélemények</h2>
            
            {/* Google Reviews Widget Mockup */}
            <div className="inline-flex flex-col items-center bg-white text-gray-900 px-8 py-6 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-2">
                <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span className="text-xl font-bold text-gray-600">Google értékelések</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-gray-900">5.0</span>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-gray-500 text-sm font-medium ml-1">| 150+ vélemény</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-3xl border border-gray-700 hover:border-gold-500/50 transition-colors shadow-xl relative group">
                <div className="absolute -top-6 left-8">
                  <img 
                    src={review.image} 
                    alt={review.name} 
                    className="w-16 h-16 rounded-full object-cover border-4 border-gray-800 shadow-lg group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="mt-8">
                  <div className="flex items-center gap-1 text-gold-400 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 text-lg italic leading-relaxed">"{review.text}"</p>
                  <div className="border-t border-gray-700 pt-4">
                    <div className="font-bold text-white text-lg">{review.name}</div>
                    <div className="text-gold-400 text-sm font-medium mb-1">{review.role}</div>
                    <div className="text-xs text-gray-500">{review.date}</div>
                  </div>
                </div>
                
                <div className="absolute bottom-4 right-4 flex items-center gap-1.5 opacity-30 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white rounded-full p-0.5">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Google Verified</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="kapcsolat" className="py-24 bg-gradient-to-br from-gold-500 to-gold-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8 drop-shadow-lg uppercase tracking-tight">
            Bajban van? Ne várjon tovább!
          </h2>
          <p className="text-2xl text-gold-100 mb-12 font-medium">
            Hívja diszpécserünket most, és indulunk segíteni.
          </p>
          <a 
            href={`tel:${phoneNumber.replace(/\s/g, '')}`}
            className="inline-flex items-center justify-center px-12 py-6 text-3xl md:text-4xl font-black text-gold-500 bg-white rounded-xl hover:bg-gray-50 transition-all transform hover:scale-105 shadow-2xl border-b-8 border-gray-300 active:border-b-0 active:translate-y-2 uppercase tracking-wider"
          >
            <Phone className="w-8 h-8 md:w-10 md:h-10 mr-4 fill-current" />
            {phoneNumber}
          </a>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-gold-100 text-sm font-medium">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Ingyenes kiszállás Budapesten
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Azonnali indulás
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Bankkártyás fizetés
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-16 border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gold-500 p-2 rounded-lg">
                  <LockOpen className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">
                  0-24SOS <span className="text-gold-500">Zárszerviz</span>
                </span>
              </div>
              <p className="text-base leading-relaxed mb-6">
                Megbízható, gyors és szakszerű zárszerviz szolgáltatás Budapesten és Pest megyében. Több mint 10 év tapasztalat, több ezer elégedett ügyfél.
              </p>
              <div className="flex gap-4">
                {/* Social icons placeholders */}
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold-500 hover:text-white transition-colors cursor-pointer">
                  <span className="font-bold">fb</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold-500 hover:text-white transition-colors cursor-pointer">
                  <span className="font-bold">ig</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider">Szolgáltatási terület</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                  <span>Budapest összes kerülete (I-XXIII.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                  <span>Pest megye és agglomeráció</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                  <span>Kiszállási idő: 30-60 perc forgalomtól függően</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider">Azonnali Kapcsolat</h3>
              <a href={`tel:${phoneNumber.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white hover:text-gold-500 transition-colors mb-4 group">
                <div className="bg-gold-500/20 p-2 rounded-full group-hover:bg-gold-500 transition-colors">
                  <Phone className="w-5 h-5 text-gold-500 group-hover:text-white" />
                </div>
                <span className="text-xl font-bold">{phoneNumber}</span>
              </a>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gray-800 p-2 rounded-full">
                  <Clock className="w-5 h-5 text-gray-400" />
                </div>
                <span>Non-stop 0-24 Ügyelet</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gray-800 p-2 rounded-full">
                  <ShieldCheck className="w-5 h-5 text-gray-400" />
                </div>
                <span>MABISZ minősített szakemberek</span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-900 pt-8 text-center text-sm flex flex-col md:flex-row justify-between items-center gap-4">
            <p>&copy; {new Date().getFullYear()} 0-24SOS Zárszerviz. Minden jog fenntartva.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Adatvédelmi tájékoztató</a>
              <a href="#" className="hover:text-white transition-colors">Impresszum</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
